import type { AiRoleTemplateView } from "../automation-types";
import type {
  StarterSetResult,
  SyncTemplatesResult,
  TemplateDetail,
  TemplatePreset,
  TemplateValidateResult,
} from "../template-types";
import type { DocTemplateView } from "../booking-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";
import type { N8nTemplateCard } from "../client-types";

export type TemplatesApiMethods = Pick<
  ApiClient,
  | "listDocTemplates"
  | "addDocTemplate"
  | "createDocTemplate"
  | "mapDocTemplate"
  | "getTemplate"
  | "createTemplate"
  | "validateTemplate"
  | "updateTemplate"
  | "editTemplateOnMeta"
  | "deleteTemplate"
  | "duplicateTemplate"
  | "submitTemplate"
  | "completeTemplateCategoryReview"
  | "syncTemplates"
  | "uploadTemplateMediaSample"
  | "improveTemplateText"
  | "createStarterSet"
  | "importLibraryTemplate"
  | "listTemplatePresets"
  | "createTemplatePreset"
  | "updateTemplatePreset"
  | "deleteTemplatePreset"
  | "listN8nTemplates"
  | "importN8nTemplate"
  | "createAutomationFromTemplate"
  | "listAiRoleTemplates"
>;

export function createTemplatesApi({ request, ws }: HttpTransport): TemplatesApiMethods {
  return {
    async listDocTemplates(id) {
      return (await request<{ templates: DocTemplateView[] }>("GET", `${ws(id)}/docs/templates`))
        .templates;
    },
    addDocTemplate: (id, docId) =>
      request<DocTemplateView>("POST", `${ws(id)}/docs/templates`, { docId }),
    createDocTemplate: (id, title, text) =>
      request<DocTemplateView>("POST", `${ws(id)}/docs/templates/new`, { title, text }),
    mapDocTemplate: (id, tplId, mappings) =>
      request<DocTemplateView>("PATCH", `${ws(id)}/docs/templates/${encodeURIComponent(tplId)}`, {
        mappings,
      }),
    getTemplate: (id, tid) =>
      request<TemplateDetail>("GET", `${ws(id)}/templates/${encodeURIComponent(tid)}`),
    createTemplate: (id, input) => request<TemplateDetail>("POST", `${ws(id)}/templates`, input),
    validateTemplate: (id, input) =>
      request<TemplateValidateResult>("POST", `${ws(id)}/templates/validate`, input),
    updateTemplate: (id, tid, patch) =>
      request<TemplateDetail>("PATCH", `${ws(id)}/templates/${encodeURIComponent(tid)}`, patch),
    editTemplateOnMeta: (id, tid, patch) =>
      request<TemplateDetail>(
        "POST",
        `${ws(id)}/templates/${encodeURIComponent(tid)}/edit-on-meta`,
        patch,
      ),
    duplicateTemplate: (id, tid) =>
      request<TemplateDetail>("POST", `${ws(id)}/templates/${encodeURIComponent(tid)}/duplicate`),
    async deleteTemplate(id, tid) {
      await request("DELETE", `${ws(id)}/templates/${encodeURIComponent(tid)}`);
    },
    submitTemplate: (id, tid) =>
      request<TemplateDetail>("POST", `${ws(id)}/templates/${encodeURIComponent(tid)}/submit`),
    completeTemplateCategoryReview: (id, tid, input) =>
      request<TemplateDetail>(
        "POST",
        `${ws(id)}/templates/${encodeURIComponent(tid)}/category-review`,
        input,
      ),
    syncTemplates: (id, channelId) =>
      request<SyncTemplatesResult>("POST", `${ws(id)}/templates/sync`, { channelId }),
    uploadTemplateMediaSample: (id, { channelId, format, file }) =>
      request<{ handle: string }>(
        "POST",
        `${ws(id)}/templates/media-sample?channelId=${encodeURIComponent(channelId)}&format=${format}`,
        undefined,
        { body: file, contentType: file.type || "application/octet-stream" },
      ),
    improveTemplateText: (id, input) =>
      request<{ text: string }>("POST", `${ws(id)}/templates/improve`, input),
    createStarterSet: (id, channelId) =>
      request<StarterSetResult>("POST", `${ws(id)}/templates/starter-set`, { channelId }),
    importLibraryTemplate: (id, input) =>
      request<TemplateDetail>("POST", `${ws(id)}/templates/library/import`, input),
    async listTemplatePresets(id, tid) {
      return (
        await request<{ items: TemplatePreset[] }>(
          "GET",
          `${ws(id)}/templates/${encodeURIComponent(tid)}/presets`,
        )
      ).items;
    },
    async createTemplatePreset(id, tid, input) {
      return (
        await request<{ preset: TemplatePreset }>(
          "POST",
          `${ws(id)}/templates/${encodeURIComponent(tid)}/presets`,
          input,
        )
      ).preset;
    },
    async updateTemplatePreset(id, presetId, patch) {
      return (
        await request<{ preset: TemplatePreset }>(
          "PATCH",
          `${ws(id)}/templates/presets/${encodeURIComponent(presetId)}`,
          patch,
        )
      ).preset;
    },
    async deleteTemplatePreset(id, presetId) {
      await request("DELETE", `${ws(id)}/templates/presets/${encodeURIComponent(presetId)}`);
    },
    listN8nTemplates: async (id) =>
      (
        await request<{ templates: N8nTemplateCard[] }>(
          "GET",
          `${ws(id)}/integrations/n8n/templates`,
        )
      ).templates,
    importN8nTemplate: (id, templateKey) =>
      request<{ workflowId: string }>("POST", `${ws(id)}/integrations/n8n/import`, {
        templateKey,
      }),
    createAutomationFromTemplate: (id, key) =>
      request<{ flowId: string; triggerId: string }>("POST", `${ws(id)}/automation-hub/templates`, {
        key,
      }),
    async listAiRoleTemplates(id) {
      return (await request<{ items: AiRoleTemplateView[] }>("GET", `${ws(id)}/ai/role-templates`))
        .items;
    },
  };
}
